import { getServiceUrl } from './api.config';
import { ApiClientOptions } from './types/api';
import ApiClient from './utils/ApiClient';
import qs from './utils/query';
import { isNotEmptyArray, isPositiveInteger } from './utils/validators';

export default class MediaApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('media'), options);
  }

  // =============================================================================
  //                              V4 asset API
  // =============================================================================

  public uploadProductPicture(file: any, payload: any = {}) {
    // This route does not support payload for now.
    return this.post('/media/v4/product/picture/upload', payload, file);
  }

  public UpsertSpecificPictureData = (
    productPicture_id: number,
    targetOrganization_id: number,
    data: object,
  ) =>
    this.post('/media/v4/products/picture/specificdata', {
      data: { productPicture_id, targetOrganization_id, data },
    });

  public UpsertSpecificDocumentData = (
    productDocument_id: number,
    targetOrganization_id: number,
    data: object,
  ) =>
    this.post('/media/v4/products/document/specificdata', {
      data: { productDocument_id, targetOrganization_id, data },
    });

  public UpsertSpecificEnrichedContentData = (
    productEnrichedContent_id: number,
    targetOrganization_id: number,
    data: object,
  ) =>
    this.post('/media/v4/products/enrichedcontent/specificdata', {
      data: { productEnrichedContent_id, targetOrganization_id, data },
    });

  public UpsertSpecificVideoData = (
    productVideo_id: number,
    targetOrganization_id: number,
    data: object,
  ) =>
    this.post('/media/v4/products/video/specificdata', {
      data: { productVideo_id, targetOrganization_id, data },
    });

  public DeleteSpecificAssets({
    targetOrganizationId,
    productKeyId,
  }: any = {}) {
    const data: any = {};

    if (isPositiveInteger(targetOrganizationId)) {
      data.target_organization_id = targetOrganizationId;
    }

    if (isPositiveInteger(productKeyId)) {
      data.product_key_id = productKeyId;
    }

    return this.delete('/media/v4/products/assets/specificdata', data);
  }

  // =============================================================================
  //                              V3 Assets API
  // =============================================================================

  public getAssets(params = {}, useCache = false) {
    const query = `/media/v3/assets`;
    return this.get(query, qs.stringify(params, true), useCache);
  }

  // =============================================================================
  //                              V2 old APIs
  // =============================================================================

  // Only used for brand (Legacy)
  public EntityStandardPost(payload: any) {
    const query = '/media/v2/picture/standard';
    return this.post(query, payload);
  }

  // =============================================================================
  //                              V3 ProductPicture API
  // =============================================================================

  public ProductPictureUpdate(picture: any) {
    const query = `/media/v3/productpicture/${picture.id}`;
    return this.put(query, picture);
  }

  public ProductPictureCreate(picture: any) {
    const query = '/media/v3/productpicture';
    return this.post(query, picture);
  }

  public ProductPictureUpload(picture: any, file: any) {
    const query = '/media/v3/productpicture/upload';
    return this.post(query, picture, file);
  }

  public EntityPictureDelete(entityPictureId: number) {
    const query = `/media/v3/productpicture/${entityPictureId}`;
    return this.delete(query);
  }

  public ProductEnrichedContentPictureUpload(picture: any, file: any) {
    const query = '/media/v3/product/enrichedcontentpicture/upload';
    return this.post(query, picture, file);
  }

  public ProductPictureDownloadAll(data: any) {
    const query = '/media/v3/product/pictures/download';
    return this.getBlob(query, '', data, true);
  }

  // =============================================================================
  //                              V3 BrandPicture API
  // =============================================================================

  public BrandPictureList(
    filters: any = {},
    useCache: boolean = false,
    limit?: number,
    offset?: number,
  ) {
    const query = '/media/v3/brandpicture';

    let parameters = '?alk=1';
    if (isNotEmptyArray(filters.brand_ids)) {
      parameters += `&filter_brand_id_in=${filters.brand_ids.join()}`;
    }

    if (isPositiveInteger(limit)) {
      parameters += `&limit=${limit}`;
    }
    if (isPositiveInteger(offset)) {
      parameters += `&offset=${offset}`;
    }
    return this.get(query, parameters, useCache);
  }

  public BrandPictureUpdate(picture: any) {
    const query = `/media/v3/brandpicture/${picture.id}`;
    return this.put(query, picture);
  }

  public BrandPictureCreate(picture: any) {
    const query = '/media/v3/brandpicture';
    return this.post(query, picture);
  }

  public BrandPictureUpload(picture: any, file: any) {
    const query = '/media/v3/brandpicture/upload';
    return this.post(query, picture, file);
  }

  public BrandPictureDelete(brandPictureId: number) {
    const query = `/media/v3/brandpicture/${brandPictureId}`;
    return this.delete(query);
  }

  // =============================================================================
  //                              V3 VIDEO API
  // =============================================================================

  public ProductVideoCreate(productVideo: any) {
    const query = '/media/v3/productvideo';
    return this.post(query, productVideo);
  }

  public ProductVideoDelete(productVideoId: number) {
    const query = `/media/v3/productvideo/${productVideoId}`;
    return this.delete(query);
  }

  public ProductVideoUpdate(productVideo: any) {
    const query = `/media/v3/productvideo/${productVideo.id}`;
    return this.put(query, productVideo);
  }
  // =============================================================================
  //                              V3 EnrichedContent API
  // =============================================================================

  public EnrichedContentCreate(enrichedContent: any) {
    const query = '/media/v3/enrichedcontent';
    return this.post(query, enrichedContent);
  }

  public EnrichedContentPut(enrichedContent: any) {
    const query = `/media/v3/enrichedcontent/${enrichedContent.id}`;
    return this.put(query, enrichedContent);
  }

  public EnrichedContentDelete(enrichedContentId: number) {
    const query = `/media/v3/enrichedcontent/${enrichedContentId}`;
    return this.delete(query);
  }

  // =============================================================================
  //                              V3 ProductDocument API
  // =============================================================================

  public ProductDocumentCreate(doc: any) {
    const query = '/media/v3/productdocument';
    return this.post(query, doc);
  }

  public ProductDocumentUpdate(doc: any) {
    const query = `/media/v3/productdocument/${doc.id}`;
    return this.put(query, doc);
  }

  public ProductDocumentDelete(docId: number) {
    const query = `/media/v3/productdocument/${docId}`;
    return this.delete(query);
  }

  public ProductDocumentUpload(doc: any, file: any) {
    const query = '/media/v3/productdocument/upload';
    return this.post(query, doc, file);
  }

  public OrganizationPictureUpload(picture: any, file: any) {
    const query = '/media/v3/organizationpicture/upload';
    return this.post(query, picture, file);
  }

  public OrganizationPictureDelete(
    organizationId: number,
    pictureType: string,
  ) {
    const query = `/media/v3/organizationpicture/${organizationId}/${pictureType}`;
    return this.delete(query);
  }

  // =============================================================================
  //                              V3 Concept API
  // =============================================================================

  public ConceptPictureUpload(conceptId: number, file: any) {
    const query = '/media/v3/concept/picture/upload';
    const payload = {
      entity_id: conceptId,
    };
    return this.post(query, payload, file);
  }
}
